import { IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useGridApiRef } from '@mui/x-data-grid';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import React from 'react';
import { useIntl } from 'react-intl';

import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import { ChartPalette } from 'components/charts/types/chart-palette.type';
import { useColumnStepScroll } from 'components/data-grid/hooks/use-column-step-scroll';

type Props = {
  apiRef: ReturnType<typeof useGridApiRef>;
};

// TODO: To share this with other grids, move it to src/components/data-grid/ next to useColumnStepScroll.
// Three things tie it to Growth today: the colors come from the chart palette instead of theme.palette,
// the aria labels use growthPage translation keys, and it only helps grids whose columns have a minWidth
// wide enough to overflow. Most grids in the app flex to fit, so they would never show the buttons.
// Fix by taking colors from theme.palette and moving the labels to a shared key such as dataGrid.scrollLeft.

const StepButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'chartPalette',
})<{ chartPalette: ChartPalette }>(({ chartPalette }) => ({
  color: chartPalette.text.primary,
  '&.Mui-disabled': { color: chartPalette.text.disabled },
}));

const GrowthTableStepButtons: React.FC<Props> = ({ apiRef }) => {
  const intl = useIntl();
  const chartPalette = useChartPalette();
  const { canScrollLeft, canScrollRight, scrollLeft, scrollRight } = useColumnStepScroll(apiRef);

  if (!canScrollLeft && !canScrollRight) return null;

  const steps = [
    { key: 'scrollLeft', disabled: !canScrollLeft, onClick: scrollLeft, icon: <ChevronLeft /> },
    { key: 'scrollRight', disabled: !canScrollRight, onClick: scrollRight, icon: <ChevronRight /> },
  ];

  return (
    <>
      {steps.map(({ key, disabled, onClick, icon }) => (
        <StepButton
          key={key}
          chartPalette={chartPalette}
          size="small"
          aria-label={intl.formatMessage({ id: `benchmarkAnalytics.growthPage.table.${key}` })}
          disabled={disabled}
          onClick={onClick}
        >
          {icon}
        </StepButton>
      ))}
    </>
  );
};

export default GrowthTableStepButtons;
